import { FileValidationResult, UploadResult, UploadableFile } from "../types";
import { FILE_UPLOAD_CONFIG } from "../config/constants";

export const ACCEPTED_FILE_TYPES = FILE_UPLOAD_CONFIG.ACCEPTED_TYPES;
export const MAX_FILE_SIZE = FILE_UPLOAD_CONFIG.MAX_FILE_SIZE;
export const MAX_FILES = FILE_UPLOAD_CONFIG.MAX_FILES;

/**
 * @description Validates a single file
 * @param file The file to validate
 * @returns A FileValidationResult object
 */
export function validateFile(file: File): FileValidationResult {
  if (!ACCEPTED_FILE_TYPES.includes(file.type as typeof ACCEPTED_FILE_TYPES[number])) {
    return {
      isValid: false,
      error: `File type ${file.type} not supported. Please use JPG, PNG, or HEIC format.`
    };
  }

  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: `File size too large. Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)}MB.`
    };
  }

  return { isValid: true };
}

/**
 * Uploads a single file with progress tracking
 * @param uploadableFile The file to upload
 * @param orderId The order ID
 * @param onFileProgress Optional callback for progress tracking
 * @returns A Promise that resolves to an UploadResult object
 */
async function uploadSingleFile(
  uploadableFile: UploadableFile,
  orderId: string,
  onFileProgress?: (slotId: string, progress: number, status: 'uploading' | 'success' | 'error', error?: string) => void
): Promise<UploadResult> {
  const { file, slotId, imagePosition, lineItemId } = uploadableFile;

  try {
    onFileProgress?.(slotId, 0, 'uploading');

    const extractIdFromGid = (gid: string) => {
      if (!gid) return '';
      const parts = gid.split('/');
      return parts[parts.length - 1];
    };

    const formdata = new FormData();
    formdata.append("order_id", extractIdFromGid(orderId));
    formdata.append("line_item_id", extractIdFromGid(lineItemId));
    formdata.append("image_position", imagePosition);
    formdata.append("image", file, file.name);

    const uploadUrl = `${FILE_UPLOAD_CONFIG.API_BASE_URL}${FILE_UPLOAD_CONFIG.UPLOAD_ENDPOINT}`;

    const response = await fetch(uploadUrl, {
      method: "POST",
      headers: {
        'Accept': 'application/json',
      },
      body: formdata,
    });

    if (!response.ok) {
      const errorText = await response.text();
      const errorMessage = errorText || `Upload failed with status ${response.status}`;
      onFileProgress?.(slotId, 0, 'error', errorMessage);

      return {
        success: false,
        error: errorMessage,
        fileName: file.name,
        fileIndex: undefined,
      };
    }

    const result = await response.text();
    onFileProgress?.(slotId, 100, 'success');

    return {
      success: true,
      url: result,
      fileName: file.name,
      fileIndex: undefined,
    };

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Upload failed';
    onFileProgress?.(slotId, 0, 'error', errorMessage);

    return {
      success: false,
      error: errorMessage,
      fileName: file.name,
      fileIndex: undefined,
    };
  }
}

/**
 * Processes files with concurrency control
 */
async function processWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  processor: (item: T) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  const executing: Promise<void>[] = [];
  let index = 0;

  const executeNext = async (): Promise<void> => {
    while (index < items.length) {
      if (executing.length >= concurrency) {
        await Promise.race(executing);
        continue;
      }

      const currentIndex = index++;
      const promise = processor(items[currentIndex])
        .then((result) => {
          results[currentIndex] = result;
        })
        .finally(() => {
          const promiseIndex = executing.indexOf(promise);
          if (promiseIndex > -1) {
            executing.splice(promiseIndex, 1);
          }
        });

      executing.push(promise);
    }

    await Promise.all(executing);
  };

  await executeNext();
  return results;
}

/**
 * Uploads an array of files with concurrency control and progress tracking
 */
export async function uploadFiles(
  files: UploadableFile[],
  orderId: string,
  onProgress?: (progress: number) => void,
  onFileProgress?: (slotId: string, progress: number, status: 'uploading' | 'success' | 'error', error?: string) => void
): Promise<UploadResult[]> {
  const CONCURRENCY_LIMIT = FILE_UPLOAD_CONFIG.CONCURRENT_UPLOADS;
  const totalFiles = files.length;

  if (totalFiles === 0) {
    return [];
  }

  let completedCount = 0;
  const updateOverallProgress = () => {
    const overallProgress = (completedCount / totalFiles) * 100;
    onProgress?.(overallProgress);
  };

  const results = await processWithConcurrency(
    files,
    CONCURRENCY_LIMIT,
    async (uploadableFile) => {
      const result = await uploadSingleFile(
        uploadableFile,
        orderId,
        (slotId, fileProgress, status, error) => {
          onFileProgress?.(slotId, fileProgress, status, error);

          if (status === 'success' || status === 'error') {
            completedCount++;
            updateOverallProgress();
          }
        }
      );

      return result;
    }
  );

  if (onProgress) {
    onProgress(100);
  }

  return results;
}